import type { VatBreakdownLine } from '@/api/models';
import { formatGel } from '@/pages/accounting/books/chartOfAccounts/utils';
import { NIL_AMOUNT } from '@/pages/accounting/statements/statementMeta';

/** Which way the net VAT points. `NIL` = nothing owed either way. */
export type NetDirection = 'PAYABLE' | 'RECLAIMABLE' | 'NIL';

/**
 * A negative net is a **reclaim** position, not an error, and this is what says
 * so. The screen prints the magnitude and lets the direction carry the meaning
 * in words — "reclaimable from budget" — because a reader who takes a bare
 * `-₾1,200.00` for a mistake has been misled by the report, not by themselves.
 */
export const netDirection = (netTetri: number): NetDirection =>
    netTetri > 0 ? 'PAYABLE' : netTetri < 0 ? 'RECLAIMABLE' : 'NIL';

/** Why the settlement action is unavailable, or `null` when it is available. */
export type SettlementBlock = 'ZERO_NET' | 'NO_SETTLEMENT_ACCOUNT' | null;

/**
 * Whether a settlement can be prepared, and if not, why — each reason stated to
 * the user rather than left as an inert button.
 *
 * - **Zero net**: there is nothing to settle. Confirmed against the API, which
 *   returns a draft with *no lines* and `TOO_FEW_LINES` for a nil period, so
 *   this refusal is the client agreeing with the server rather than guessing.
 * - **No settlement account**: the server resolves the settlement account by
 *   code and refuses to build anything without it. The report reports that
 *   directly — `settlementAccount.accountCode` is `null` — so the action is
 *   disabled for a reason the user can act on, instead of failing on press.
 */
export const settlementBlock = (
    netTetri: number,
    settlementAccountCode: string | null,
): SettlementBlock => {
    if (settlementAccountCode === null) {
        return 'NO_SETTLEMENT_ACCOUNT';
    }
    return netTetri === 0 ? 'ZERO_NET' : null;
};

/**
 * The settlement's statutory deadline: the **15th of the month following the
 * period's end**. Informational only — nothing here files anything.
 *
 * Mirrors payroll's `filingDueDate`, which does the same for a payroll month.
 * `''` for an unreadable date rather than a fabricated deadline.
 */
export const settlementDueDate = (periodEndIso: string): string => {
    const year = Number(periodEndIso.slice(0, 4));
    const month = Number(periodEndIso.slice(5, 7));
    if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1) {
        return '';
    }
    const rollsOver = month === 12;
    const dueYear = rollsOver ? year + 1 : year;
    const dueMonth = rollsOver ? 1 : month + 1;
    return `${dueYear}-${String(dueMonth).padStart(2, '0')}-15`;
};

/**
 * The date a settlement entry itself takes: the last day of the period.
 *
 * Sent explicitly on every build, because the server binds an omitted `date` to
 * `0001-01-01` and hands back a draft dated in year 1.
 */
export const settlementEntryDate = (periodEndIso: string): string =>
    periodEndIso;

/** One account a drill affordance can open the journal on. */
export type VatDrillTarget = {
    accountId: string;
    accountCode: string;
    accountName: string;
    amountText: string;
};

/** A render-ready breakdown row for one contributing account. */
export type VatRow = {
    accountId: string;
    code: string;
    label: string;
    amountText: string;
    /**
     * Read from the figure, not from the formatted string — `formatGel` puts the
     * sign *after* the currency symbol (`₾ -3,200.00`), so a prefix test would
     * silently never fire.
     */
    isNegative: boolean;
    /**
     * The accounts this row's drill can open — `[]` when there is nothing to
     * drill to, which renders **no affordance at all**.
     *
     * A per-account row has at most one target, and has none when the server
     * sent no contributing entries for it: an account that is tagged for VAT but
     * moved by nothing in this period would open a journal view with no rows in
     * it. A link that goes nowhere is the exact bug this integration was meant
     * to fix, and a second variety of it would be worse than the first.
     */
    drillTargets: VatDrillTarget[];
};

const drillTargetOf = (line: VatBreakdownLine): VatDrillTarget => ({
    accountId: line.accountId,
    accountCode: line.accountCode,
    accountName: line.accountName,
    amountText: formatGel(line.amountTetri),
});

/** Turn a side's contributing accounts into render rows (code + name + amount). */
export const toVatRows = (lines: VatBreakdownLine[]): VatRow[] =>
    lines.map((line) => ({
        accountId: line.accountId,
        code: line.accountCode,
        // The server resolved the name for the requested locale already.
        label: line.accountName || line.accountCode,
        amountText:
            line.amountTetri === 0 ? NIL_AMOUNT : formatGel(line.amountTetri),
        isNegative: line.amountTetri < 0,
        drillTargets: line.entries.length > 0 ? [drillTargetOf(line)] : [],
    }));

/**
 * The drill for a **side total**, which is the multi-account case: a side is
 * composed of every account that contributed to it, and the journal filters one
 * account at a time — so the reader picks. Output VAT can easily span more than
 * one account, and this is where that shows up.
 *
 * Accounts with no contributing entries are left out for the same reason they
 * get no row-level affordance: there is nothing behind them to open. A side
 * whose accounts all moved by nothing yields `[]`, and the total row draws no
 * affordance at all.
 */
export const toSideDrillTargets = (
    lines: VatBreakdownLine[],
): VatDrillTarget[] =>
    lines.filter((line) => line.entries.length > 0).map(drillTargetOf);
